import { get, has } from "lodash-es";
import { Ref } from "react";
import {
  Control,
  DeepMap,
  FieldError,
  RegisterOptions,
  Validate,
} from "react-hook-form";

import { PeoplePicker, SinglePeoplePicker } from "Bands/containers";
import { copy } from "shared/assets";
import {
  CountrySelect,
  Fieldset,
  IFileUploadValue,
  InputField,
  RTE,
  TextArea,
  TimezoneSelect,
  Title,
  UnitConverter,
} from "shared/components";
import { FIELD_SIZES } from "shared/constants";
import { MutedText, SmallText } from "shared/styles";
import {
  IAsyncSelectField,
  ICheckboxGroupField,
  IDatePickerField,
  IDraggableCheckboxGroup,
  IFieldType,
  IFileField,
  IFileValidation,
  IFormComponent,
  IFormField,
  IFormFieldset,
  IFormWidget,
  IFormWidgetType,
  IPasswordField,
  IRTEField,
  IRadioGroupField,
  ISelectField,
  ISimpleDateField,
  ISimpleTimeField,
  ITagGroup,
  IUnitType,
  IWebsiteField,
} from "shared/types";
import { breakpointMatches, isValidUrl } from "shared/utils";
import {
  AsyncSelect,
  Checkbox,
  CheckboxGroup,
  ColorField,
  DatePickerField,
  DimensionField,
  FileUpload,
  FilesUpload,
  ImageUpload,
  NumberField,
  OtherTextField,
  PasswordField,
  PhoneField,
  RadioGroup,
  SearchField,
  Select,
  SimpleDateField,
  TagGroup,
  VATField,
} from "../../Fields";
import { COLOR_REGEX } from "../../Fields/ColorField/utils";
import { SimpleTimeField2 } from "../../Fields/DateField/SimpleTimeField2";
import { DraggableCheckboxGroup } from "../../Fields/Option/DraggableCheckboxGroup";
import { MultiCreatableSelect } from "../../Fields/select/components/MultiCreatableSelect";
import { FieldWrapperContainer } from "../../Fields/styles";

type args = [
  control: Control<Record<string, any>>,
  register: (rules?: RegisterOptions) => Ref<any>,
  errors: DeepMap<Record<string, any>, FieldError>,
  watchAllFields: {
    [x: string]: any;
  },
  trigger: (name: string) => Promise<boolean>,
  unitType?: IUnitType,
  setFieldValue?: (field: string, value: string | string[] | null) => void
];

const renderInputGroup = (
  { name, fields, path, ...rest }: IFormFieldset,
  ...args: args
) => {
  let currentPath: string[] = [];

  if (path) {
    currentPath = currentPath.concat(path);
  }

  if (name) {
    currentPath.push(name);
  }

  return (
    <Fieldset {...rest} key={name}>
      {fields.map((field) =>
        getFormItem(
          {
            path: currentPath,
            ...field,
          },
          ...args
        )
      )}
    </Fieldset>
  );
};

const renderWidget = ({ name, label, widget, style, props }: IFormWidget) => {
  let content = null;

  switch (widget) {
    case IFormWidgetType.unitConverter:
      content = <UnitConverter />;
      break;
    case IFormWidgetType.title:
      content = (
        <Title asComponent="h3" size="s">
          {label}
        </Title>
      );
      break;
    case IFormWidgetType.note:
      content = <SmallText as={MutedText}>{label}</SmallText>;
      break;
    default: {
      break;
    }
  }

  const isMobile = breakpointMatches();

  return (
    <FieldWrapperContainer
      key={name}
      style={isMobile && style?.mobile ? style.mobile : style}
    >
      {content}
    </FieldWrapperContainer>
  );
};

const renderComponent = ({ name, component, style }: IFormComponent) => {
  const isMobile = breakpointMatches();

  return (
    <FieldWrapperContainer
      key={name}
      style={isMobile && style?.mobile ? style.mobile : style}
    >
      {component}
    </FieldWrapperContainer>
  );
};

export const getFormItem = (
  item: IFormField | IFormFieldset | IFormWidget | IFormComponent,
  ...args: args
) => {
  if ("widget" in item) {
    return renderWidget(item);
  }

  if ("fields" in item) {
    return renderInputGroup(item, ...args);
  }

  if ("component" in item) {
    return renderComponent(item);
  }

  return getFormField(item, ...args);
};

const getFormField = (
  formField: IFormField,
  ...[
    control,
    register,
    errors,
    watchAllFields,
    trigger,
    unitType,
    setFieldValue,
  ]: args
): JSX.Element => {
  const {
    name,
    path,
    type,
    validation = {},
    style,
    onChange,
    ...rest
  } = formField;
  const currentPath = path ? `${path.join(".")}.${name}` : name;
  let maxLength: number | undefined;

  // set default max length for text
  if (
    !validation.maxLength &&
    (type === IFieldType.text ||
      type === IFieldType.email ||
      type === IFieldType.password ||
      type === IFieldType.website ||
      type === IFieldType.phone ||
      type === IFieldType.vat ||
      type === IFieldType.search)
  ) {
    validation.maxLength = FIELD_SIZES.TEXT;
  }

  // set default max for numbers
  if (!validation.max && type === IFieldType.number) {
    validation.max = FIELD_SIZES.NUMBER;
  }

  if (typeof validation.maxLength === "number") {
    maxLength = validation.maxLength;
  } else if (
    validation.maxLength &&
    typeof validation.maxLength !== "string" &&
    has(validation.maxLength, "value")
  ) {
    maxLength = validation.maxLength.value as number;
  }

  const props = {
    name: currentPath,
    required: !!validation?.required,
    error: get(errors, currentPath),
    errors,
    key: name,
    style: style || { width: "100%" },
    maxLength,
    onChange,
    ...rest,
  };

  if (
    type === IFieldType.select ||
    type === IFieldType.asyncSelect ||
    type === IFieldType.timezone
  ) {
    if (!validation.validate && validation.required) {
      validation.validate = (selectedValue) => {
        return !!selectedValue || copy.errors.required;
      };
    }
  }

  switch (type) {
    case IFieldType.email: {
      validation.pattern = {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: copy.errors.email,
      };

      return (
        <InputField
          type={IFieldType.email}
          registerRef={register(validation)}
          {...props}
        />
      );
    }
    case IFieldType.website: {
      validation.validate = (value: string) =>
        !value ||
        isValidUrl(value) ||
        ((rest as IWebsiteField).hideErrorMessage
          ? false
          : copy.errors.website);

      return (
        <InputField type={type} registerRef={register(validation)} {...props} />
      );
    }
    case IFieldType.password: {
      const match = (formField as IPasswordField).validation?.match;
      const matchPath =
        path && match?.name ? `${path.join(".")}.${match?.name}` : match?.name;

      const validateMatch = (value: string) =>
        value === get(watchAllFields, matchPath || "") || match?.message;
      validation.validate = validateMatch;

      return (
        <PasswordField
          type={type}
          registerRef={register(validation)}
          {...props}
        />
      );
    }
    case IFieldType.checkbox: {
      return (
        <Checkbox registerRef={register(validation)} value={name} {...props} />
      );
    }
    case IFieldType.country: {
      return (
        <CountrySelect
          control={control}
          validation={validation}
          value={get(watchAllFields, currentPath) as string}
          setFieldValue={setFieldValue}
          {...props}
        />
      );
    }
    case IFieldType.phone: {
      return (
        <PhoneField
          control={control}
          registerRef={register(validation)}
          value={get(watchAllFields, currentPath)}
          {...props}
        />
      );
    }
    case IFieldType.dimension: {
      return (
        <DimensionField
          unitType={unitType}
          control={control}
          validation={validation}
          value={get(watchAllFields, currentPath)}
          {...props}
        />
      );
    }
    case IFieldType.textarea: {
      const { rows, cols }: any = props.style || {};
      return (
        <TextArea
          {...props}
          rows={rows}
          cols={cols}
          registerRef={register(validation)}
        />
      );
    }
    case IFieldType.select: {
      const options = (formField as ISelectField).options;

      return (
        <Select
          control={control}
          validation={validation}
          value={get(watchAllFields, currentPath) as string}
          {...props}
          options={options}
          setFieldValue={setFieldValue}
        />
      );
    }
    case IFieldType.asyncSelect: {
      const options = (formField as IAsyncSelectField).options;

      return (
        <AsyncSelect
          control={control}
          validation={validation}
          value={get(watchAllFields, currentPath) as string}
          {...props}
          options={options}
          setFieldValue={setFieldValue}
        />
      );
    }
    case IFieldType.multiCreatableSelect: {
      return (
        <MultiCreatableSelect
          control={control}
          validation={validation}
          value={get(watchAllFields, currentPath) as string}
          {...props}
          options={[]}
          setFieldValue={setFieldValue}
        />
      );
    }
    case IFieldType.timezone: {
      return (
        <TimezoneSelect
          control={control}
          value={get(watchAllFields, currentPath)}
          setFieldValue={setFieldValue}
          validation={validation}
          {...props}
        />
      );
    }
    case IFieldType.number: {
      validation.valueAsNumber = true;
      return (
        <NumberField
          control={control}
          validation={validation}
          value={get(watchAllFields, currentPath)}
          min={validation.min as number}
          max={validation.max as number}
          {...props}
        />
      );
    }
    case IFieldType.file: {
      validation.validate = addFileValidation(
        (formField as IFileField).validation
      );

      return (
        <FileUpload
          control={control}
          validation={validation}
          value={get(watchAllFields, currentPath)}
          {...props}
        />
      );
    }
    case IFieldType.files: {
      validation.validate = addFileValidation(
        (formField as IFileField).validation
      );

      return (
        <FilesUpload
          control={control}
          validation={validation}
          value={get(watchAllFields, `${currentPath}`)}
          {...props}
        />
      );
    }
    case IFieldType.image: {
      validation.validate = addFileValidation(
        (formField as IFileField).validation
      );
      return (
        <ImageUpload
          control={control}
          validation={validation}
          value={get(watchAllFields, currentPath)}
          {...props}
        />
      );
    }
    case IFieldType.checkboxGroup: {
      return (
        <CheckboxGroup
          control={control}
          validation={validation}
          {...props}
          options={(formField as ICheckboxGroupField).options}
        />
      );
    }
    case IFieldType.draggableCheckboxGroup: {
      return (
        <DraggableCheckboxGroup
          control={control}
          validation={validation}
          options={(formField as IDraggableCheckboxGroup).options}
          {...props}
        />
      );
    }
    case IFieldType.radioGroup: {
      return (
        <RadioGroup
          registerRef={register(validation)}
          {...props}
          options={(formField as IRadioGroupField).options}
        />
      );
    }
    case IFieldType.search: {
      return <SearchField registerRef={register(validation)} {...props} />;
    }
    case IFieldType.color: {
      validation.pattern = {
        value: COLOR_REGEX,
        message: copy.errors.color,
      };
      return (
        <ColorField
          control={control}
          validation={validation}
          value={get(watchAllFields, currentPath)}
          {...props}
        />
      );
    }
    case IFieldType.rte: {
      return (
        <RTE
          control={control}
          validation={validation}
          defaultValue={get(watchAllFields, currentPath)}
          excludeItems={(formField as IRTEField).excludeItems}
          {...props}
        />
      );
    }
    case IFieldType.simpleDate: {
      const { dateFormat } = formField as ISimpleDateField;

      return (
        <SimpleDateField
          control={control}
          validation={validation}
          defaultValue={get(watchAllFields, currentPath)}
          dateFormat={dateFormat}
          {...props}
        />
      );
    }
    // TODO
    case IFieldType.simpleTime: {
      const timeFormat = (formField as ISimpleTimeField).timeFormat;

      return (
        <SimpleTimeField2
          control={control}
          validation={validation}
          timeFormat={timeFormat}
          defaultValue={get(watchAllFields, currentPath)}
          {...props}
        />
      );
    }
    case IFieldType.datePicker: {
      const { dateFormat } = formField as IDatePickerField;

      return (
        <DatePickerField
          control={control}
          validation={validation}
          defaultValue={get(watchAllFields, currentPath)}
          dateFormat={dateFormat}
          {...props}
        />
      );
    }
    case IFieldType.tagGroup: {
      return (
        <TagGroup
          {...props}
          control={control}
          validation={validation}
          defaultValue={get(watchAllFields, currentPath)}
          options={(formField as ITagGroup).options}
        />
      );
    }
    case IFieldType.peoplePicker: {
      return (
        <PeoplePicker
          {...props}
          control={control}
          validation={validation}
          value={get(watchAllFields, currentPath)}
          setFieldValue={setFieldValue}
        />
      );
    }
    case IFieldType.singlePeoplePicker: {
      return (
        <SinglePeoplePicker
          {...props}
          control={control}
          validation={validation}
          value={get(watchAllFields, currentPath)}
          setFieldValue={setFieldValue}
        />
      );
    }
    case IFieldType.vat: {
      return (
        <VATField
          {...props}
          control={control}
          validation={validation}
          defaultValue={get(watchAllFields, currentPath)}
        />
      );
    }
    case IFieldType.otherText: {
      return (
        <OtherTextField
          {...props}
          control={control}
          validation={validation}
          value={get(watchAllFields, currentPath)}
        />
      );
    }
    default: {
      return (
        <InputField type={type} registerRef={register(validation)} {...props} />
      );
    }
  }
};

// add file api upload validation
const addFileValidation = (
  validation: IFileValidation | undefined
): Validate | undefined => {
  return (file?: IFileUploadValue) => {
    // api upload validation error
    if (file?.error) {
      return file.error;
    }

    return true;
  };
};
